import React, { useEffect, useState } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { useIntl } from 'react-intl';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import {
  loadingCounterAtom,
  userDataAtom,
  userBranchesAtom,
  selectedBranchAtom,
  themeAtom,
} from './Atoms';
import useAPI from './api';
import Loading from './Components/Loading';
import CounterManager from './Services/CounterManager';
import SnackbarAdvise from './Components/SnackbarAdvise';
import IncomingCallPopup from './Components/Popup/IncomingCallPopup';
import useAuthAPI from './api/auth';
import ErrorDialog from './Components/Error';
import LoginRouter from './Pages/Login/LoginRouter';
import CallHubProvider from './Components/Hubs/CallHubProvider';
import ChatHubProvider from './Components/Hubs/ChatHubProvider';
import PriorityHubProvider from './Components/Hubs/PriorityHubProvider';
import EmailHubProvider from './Components/Hubs/EmailHubProvider';
import DBEMainApp from './Components';
import useTheme from './Theme/useTheme';
import pJson from '../package.json';
import './Styles/app.scss';
import { askNotificationPermission } from './Helpers/notifications';
import AlertDialog from './Components/Dialog/AlertDialog';
import { APP_ROUTES } from './Router/routes';

const App = function App() {
  const currentVersion = pJson.version;
  const currentTheme = useAtomValue(themeAtom);
  const theme = useTheme(currentTheme);
  /**Hooks calls**/
  const API = useAPI();
  const AuthAPI = useAuthAPI();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  /****/

  /**States declaration**/
  const [error, setError] = useState(null);
  const [userRoles, setUserRoles] = useState(null);
  /****/

  /**Atoms**/
  const [userBranches, setUserBranches] = useAtom(userBranchesAtom);
  const loadingCounter = useAtomValue(loadingCounterAtom);
  const [userData, setUserData] = useAtom(userDataAtom);
  const [selectedBranch, setSelectedBranch] = useAtom(selectedBranchAtom);

  /****/

  /**Login**/
  const doLogin = () => {
    AuthAPI.getUserInfo(
      (result) => {
        setUserData(result);
        AuthAPI.getUserRoles((roles) => {
          //result is array of objects
          setUserRoles(roles);
          askNotificationPermission();
        });
      },
      () => {
        AuthAPI.logout();
      }
    );
  };

  useEffect(() => {
    if (userRoles && userRoles[0].roleId === 'ACCOUNTING') {
      navigate(APP_ROUTES.PURCHASEHISTORY);
    }
  }, [userRoles]);

  useEffect(() => {
    // const { TRACKING_CODE } = window.databeasy || {};

    const _mtm = (window._mtm = window._mtm || []);
    _mtm.push({ 'mtm.startTime': new Date().getTime(), event: 'mtm.Start' });

    /*     const d = document,
      g = d.createElement('script'),
      s = d.getElementsByTagName('script')[0];
    g.async = true;
    g.src = `https://matomo.inxpire.it/js/container_${TRACKING_CODE}.js`;
    s.parentNode.insertBefore(g, s); */
  }, []);

  useEffect(() => {
    if (
      !location?.pathname?.includes('/landingPage') &&
      !location?.pathname?.includes('/callToAction')
    )
      doLogin();
  }, []);
  /****/

  useEffect(() => {
    if (userData && !userBranches) {
      API.getLoggedUserBranches(
        (result) => {
          if (result.length === 0) {
            setError(intl.messages.USER_BRANCH_NOTFOUND);
            AuthAPI.logout();
          } else {
            setUserBranches(result);
            setSelectedBranch(
              JSON.parse(sessionStorage.getItem('selectedBranch')) ||
                result.find((x) => x.isDefault)
            );
          }
        },
        (err) => {
          setError(err);
        }
      );
    }
  }, [userData, userBranches]);

  const onChangePassword = () => {
    setSelectedBranch(null);
    setUserBranches(null);
    setUserData(null);
    AuthAPI.logout();
    navigate('/'); //login page
  };

  const onLogout = () => {
    AuthAPI.logout();
    setUserData(null);
    setSelectedBranch(null);
    setUserBranches(null);
  };

  useEffect(() => {
    if (selectedBranch?.id) {
      API.getBranchSmtpEmail(selectedBranch?.id).then((res) => {
        setSelectedBranch({ ...selectedBranch, smtp: res?.data });
      });
    }
  }, [selectedBranch?.id]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {error && (
        <ErrorDialog
          error={error}
          handleClose={() => {
            setError(null);
          }}
        />
      )}
      {userData && selectedBranch && (
        <>
          <ChatHubProvider channel={selectedBranch?.id} />
          <CallHubProvider channel={selectedBranch?.id} />
          <EmailHubProvider channel={selectedBranch?.id} />
        </>
      )}
      <PriorityHubProvider />
      <Loading
        open={Boolean(CounterManager.counter)}
        loadingCounter={loadingCounter}
      />
      <SnackbarAdvise />
      <AlertDialog />
      <IncomingCallPopup />
      {selectedBranch && userData ? (
        <DBEMainApp
          loggedUserId={userData.id}
          loggedUserFullName={`${userData.name} ${userData.surname}`}
          onLogout={onLogout}
          onChangePassword={onChangePassword}
          userRoles={userRoles}
          selectedBranchId={selectedBranch?.id}
          currentVersion={currentVersion}
        />
      ) : (
        <div />
      )}
      {!userData && <LoginRouter onLogin={doLogin} />}
    </ThemeProvider>
  );
};

export default App;
